import type { EquipmentStatus } from '../../types';
import type { EquipmentPortVisual, Vec3 } from '../equipmentPortTransfer';

export type FabToolType = 'etch' | 'cvd' | 'pvd' | 'clean' | 'metrology';

export type FabToolDetail = 'high' | 'medium' | 'low' | 'very-low';

/** Accepts AMHS status codes or lowercase aliases (`running`, `idle`, …). */
export type FabToolStatus = EquipmentStatus | 'running' | 'idle' | 'warning' | 'error' | 'offline';

export function normalizeFabToolStatus(status?: FabToolStatus): EquipmentStatus {
  switch (status) {
    case 'RUNNING':
    case 'running':
      return 'RUNNING';
    case 'WARNING':
    case 'warning':
      return 'WARNING';
    case 'ERROR':
    case 'error':
      return 'ERROR';
    case 'OFFLINE':
    case 'offline':
      return 'OFFLINE';
    default:
      return 'IDLE';
  }
}

export type FabProcessEquipmentProps = {
  id?: string;
  type?: FabToolType;
  /** Number of front load ports (1–4). */
  ports?: number;
  width?: number;
  depth?: number;
  height?: number;
  status?: FabToolStatus;
  /** 0-based occupied port indices. */
  occupiedPorts?: number[];
  /** Per-port shutter open 0…1. */
  shutterOpen?: number[];
  /** Live transfer state from simulation (optional). */
  portVisuals?: EquipmentPortVisual[];
  /** Single animated FOUP in equipment-local space. */
  flyingFoup?: { position: Vec3; scaleHint?: number; foupId?: string } | null;
  /** FOUP resting in upper chamber (equipment-local). */
  chamberFoupPosition?: Vec3 | null;
  /** All chamber FOUPs with distinct slot positions. */
  chamberFoups?: Array<{ foupId?: string; position: Vec3; slotIndex: number }>;
  /** Socket-authoritative FOUP capacity. */
  foupCapacity?: number;
  /** 0–1 lifter carriage height during load-port → chamber motion. */
  lifterProgress?: number;
  /** Lifter horizontal/depth offsets (ProcessChamber-local). */
  lifterForkLocalX?: number;
  lifterForkLocalZ?: number;
  /** Per-port FOUP seat positions on the load deck (equipment-local). */
  loadPortSeats?: Vec3[];
  portFractions?: readonly number[];
  detail?: FabToolDetail;
  /** When true, distance from camera selects detail tier (0–15 / 15–40 / 40–100 / 100+ m). */
  autoDetail?: boolean;
  opacity?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
  onSelect?: () => void;
};

/** Tall compact process tool — height/width ≈ 1.56 (reference proportions). */
export const FAB_TOOL_DEFAULTS = {
  width: 3.2,
  depth: 2.2,
  height: 5.0,
} as const;

export function fabToolTypeLabel(type: FabToolType): string {
  switch (type) {
    case 'etch':
      return 'ETCH';
    case 'cvd':
      return 'CVD';
    case 'pvd':
      return 'PVD';
    case 'clean':
      return 'CLEAN';
    case 'metrology':
      return 'METROLOGY';
    default:
      return 'PROCESS';
  }
}

export function clampPortCount(ports: number): number {
  return Math.max(1, Math.min(4, Math.round(ports)));
}
